import { z } from 'zod'
import { makeFieldKeys, validateWithZodSchema } from '../../core/utils/utilities.ts'
import { TABLE_FUNDS, FIELD_KEY_SEPARATOR } from '../globals.ts'

const ALIAS_FUNDS = `${TABLE_FUNDS}${FIELD_KEY_SEPARATOR}` as const

const F = <N extends string>(name: N) => `${ALIAS_FUNDS}${name}` as const

/**
 * FundRecord — the shape of a fund row as returned by view__funds_tablegrid
 * and view__fund_dashboard_detail. Both RPCs share the funds__-prefixed
 * identity/date/capital keys; the dashboard detail RPC adds more (cashflows,
 * irr, etc.) which are passed through unchanged.
 *
 * Schema is intentionally lightweight — it enumerates the funds__-prefixed
 * fields we depend on for selectors / tab titles / form display, and uses
 * .passthrough() so the dozens of at__get_measures fields (cost_incurred,
 * fmv, moic, etc.) and the Python-added irr fields flow through without
 * needing to be redeclared here.
 */
export const FundRecordSchema = z
  .object({
    [F('id')]: z.string().nullish(),
    [F('name')]: z.string().nullish(),
    [F('legal_name')]: z.string().nullish(),
    [F('initial_closing_date')]: z.string().nullish(),
    [F('initial_closing_date_fmt')]: z.string().nullish(),
    [F('final_closing_date')]: z.string().nullish(),
    [F('final_closing_date_fmt')]: z.string().nullish(),
    [F('investment_period_end_date')]: z.string().nullish(),
    [F('investment_period_end_date_fmt')]: z.string().nullish(),
    [F('committed_capital')]: z.union([z.string(), z.number()]).nullish(),
    [F('committed_capital_fmt')]: z.string().nullish(),
    [F('closed_capital')]: z.union([z.string(), z.number()]).nullish(),
    [F('closed_capital_fmt')]: z.string().nullish(),
    [F('called_capital')]: z.union([z.string(), z.number()]).nullish(),
    [F('called_capital_fmt')]: z.string().nullish(),
    [F('avg_entry_valuation')]: z.union([z.string(), z.number()]).nullish(),
    [F('avg_entry_valuation_fmt')]: z.string().nullish()
  })
  .passthrough()

export const FundRecordFields = makeFieldKeys(FundRecordSchema)

export type FundRecord = z.infer<typeof FundRecordSchema>

export function validateFundRecord(data: unknown): FundRecord {
  try {
    return validateWithZodSchema(FundRecordSchema, data, 'fund record')
  } catch (error) {
    throw new Error(`Fund record validation failed: ${error}`)
  }
}
